/**
 * MarketplaceHomeCard - "Mercado da Forja em movimento"
 * Full-width card with live metrics and activity timestamps
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Coins, ArrowRight, Settings, MessageSquare, CheckCircle2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useMarketplaceGlobalMetrics } from '@/hooks/useMarketplaceGlobalMetrics';
import type { EffectiveToolStatus } from '@/components/ToolCard';

interface MarketplaceHomeCardProps {
  effectiveStatus?: EffectiveToolStatus;
}


function MarketplaceHomeCardInner({
  effectiveStatus,
}: MarketplaceHomeCardProps) {
  const { metrics } = useMarketplaceGlobalMetrics();
  const isBlocked = effectiveStatus?.status === 'disabled' || effectiveStatus?.status === 'locked';
  const blockedMessage = effectiveStatus?.status === 'locked'
    ? 'Você não tem acesso a esta seção no momento.'
    : effectiveStatus?.message || 'Esta seção está passando por atualizações e será liberada em breve.';

  const metricItems = [
    { icon: MessageSquare, label: 'Negociações', value: metrics.activeNegotiations, color: 'text-amber-400' },
    { icon: CheckCircle2, label: 'Vendas', value: metrics.completedNegotiations, color: 'text-emerald-400' },
    { icon: Package, label: 'Anúncios', value: metrics.activeListings, color: 'text-foreground' },
  ];


  return (
    <div className={cn(
      "relative overflow-hidden rounded-xl p-3 md:p-4 h-full",
      "forja-card",
      "transition-all duration-300 flex flex-col"
    )}>
      {/* Overlay when blocked */}
      {isBlocked && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm rounded-xl p-4">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-3 border border-amber-500/20">
            <Settings className="w-6 h-6 text-amber-500 animate-spin-slow" />
          </div>
          <h3 className="font-display font-bold text-sm text-foreground mb-1">
            {effectiveStatus?.status === 'locked' ? 'Acesso Restrito' : 'Em Atualização'}
          </h3>
          <p className="text-xs text-muted-foreground text-center max-w-[200px]">
            {blockedMessage}
          </p>
        </div>
      )}

      <div className="relative z-10 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20">
            <Coins className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
          </div>
          <div>
            <h2 className="font-display font-bold text-sm md:text-base text-foreground">
              Mercado da Forja em movimento
            </h2>
            <p className="text-[9px] md:text-[10px] text-muted-foreground">
              Compradores e vendedores negociando contas todos os dias.
            </p>
          </div>
        </div>

        {/* Metrics + CTA centered */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center">
            <div className="grid grid-cols-3 gap-1.5 w-full">
              {metricItems.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg bg-secondary/40 border border-border/30"
                >
                  <item.icon className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className={cn("text-base font-bold", item.color)}>{item.value}</span>
                  <span className="text-[8px] md:text-[9px] text-muted-foreground text-center leading-tight">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-auto pt-2">
            <Link to="/marketplace" className="block">
              <Button size="sm" className="w-full gap-1.5 text-xs">
                Abrir Mercado
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const MarketplaceHomeCard = React.memo(MarketplaceHomeCardInner);
export default MarketplaceHomeCard;
